import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { buildCandidateEvents, buildEmployerEvents } from "../api/services/notifications";
import { useSuperApp } from "./useSuperApp";
import { useAccountRole } from "../context/RoleContext";
import { useEmployerJobs } from "./useEmployerJobs";
import { getUserKey } from "./useAppliedJobs";
import {
  isFirstRun,
  markAllSeen as storeMarkAllSeen,
  markRead as storeMarkRead,
  readState,
  subscribe,
  writeState,
} from "../utils/notificationStore";

/** Long enough that a quiet account costs almost nothing; short enough to feel live. */
const REFRESH_MS = 5 * 60 * 1000;



/**
 * The notifications feed and its unread count.
 *
 * Mounted twice on purpose — once by the header for the bell, once by the page — and both
 * share one React Query entry, so the second costs no requests.
 *
 * Which sources are read depends on the active role, not on what the account could be: an
 * employer looking at their ads is not also shown their own jobseeker stage changes, which
 * would be a different person's news on the same screen.
 */
export const useNotifications = () => {
  const { isAuthenticated, isAuthLoading, currentUser } = useSuperApp();
  const { accountRole } = useAccountRole();
  const isEmployer = accountRole === "employer";

  const userKey = getUserKey(currentUser);

  // The employer feed is built from ads React Query already holds for Миний зар. Held back
  // for a jobseeker: /diil/employer/jobs is authenticated and would 403 for them.
  const { posts } = useEmployerJobs({ enabled: isAuthenticated && isEmployer });

  // Re-read after every write so the badge reflects a row that was just opened. State
  // rather than a ref: the count is rendered, so it has to trigger one.
  const [state, setState] = useState(() => readState(userKey));
  useEffect(() => {
    setState(readState(userKey));
  }, [userKey]);

  // Every other copy of this hook writing the store is this copy's business too: the badge
  // in the header has to clear the moment the list marks things seen, and it is a separate
  // mount with a separate useState.
  useEffect(() => subscribe((changedKey, next) => {
    if (changedKey === userKey) setState(next);
  }), [userKey]);

  // Snapshots are read at fetch time but must not re-trigger the query when they change —
  // the fetch itself rewrites them, and a dependency would loop.
  const stateRef = useRef(state);
  stateRef.current = state;

  const enabled = isAuthenticated && !isAuthLoading && !!userKey;

  const query = useQuery({
    queryKey: ["notifications", accountRole, userKey],
    enabled,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchInterval: REFRESH_MS,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const stored = stateRef.current;

      if (isEmployer) {
        const { events, applicantSnapshot } = await buildEmployerEvents({
          posts,
          applicantSnapshot: stored.applicantSnapshot,
        });
        return { events, snapshots: { applicantSnapshot } };
      }

      const { events, statusSnapshot, suggestSnapshot } = await buildCandidateEvents({
        statusSnapshot: stored.statusSnapshot,
        suggestSnapshot: stored.suggestSnapshot,
      });
      return { events, snapshots: { statusSnapshot, suggestSnapshot } };
    },
  });

  const events = useMemo(() => {
    const list = query.data?.events || [];
    return [...list].sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [query.data]);

  // Storing the snapshots is a side effect of having fetched, so it happens here rather
  // than inside queryFn — a retried or deduplicated fetch must not write twice.
  useEffect(() => {
    const snapshots = query.data?.snapshots;
    if (!snapshots || !userKey) return;

    const stored = stateRef.current;
    const patch = { ...snapshots };

    // First run on this device: everything visible is history, not news. Without this the
    // bell would open on a month of old rows the moment someone signs in.
    if (isFirstRun(stored)) {
      patch.lastSeenAt = new Date().toISOString();
    }

    setState(writeState(userKey, patch));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, userKey]);

  const readIds = useMemo(() => new Set(state.readIds || []), [state.readIds]);

  const items = useMemo(
    () =>
      events.map((event) => ({
        ...event,
        unread: !readIds.has(event.id) && (!state.lastSeenAt || new Date(event.at) > new Date(state.lastSeenAt)),
      })),
    [events, readIds, state.lastSeenAt]
  );

  const unreadCount = useMemo(() => {
    // The true count. Capping it here would make "99+" unreachable in the badge, which is
    // the one place that has to decide how many digits it can show.
    return items.filter((item) => item.unread).length;
  }, [items]);

  const markAllSeen = useCallback(() => {
    if (!userKey) return;
    setState(storeMarkAllSeen(userKey));
  }, [userKey]);

  const markRead = useCallback(
    (id) => {
      if (!userKey) return;
      setState(storeMarkRead(userKey, id));
    },
    [userKey]
  );

  return {
    items,
    unreadCount,
    isLoading: query.isLoading && enabled,
    isError: query.isError,
    markAllSeen,
    markRead,
    refresh: query.refetch,
  };
};

export default useNotifications;
